import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, BarChart3, CreditCard, Shield, ShieldCheck, ShieldX, ToggleLeft, ToggleRight, Loader2, Key, Save, TrendingUp, Leaf, Mail, Calendar, Settings, Database, Search, Plus, X, Copy, Trash2, Ban, RotateCcw, Clock, UserX, AlertTriangle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { authFetch } from "@/lib/api";
import UserParcelsSection from "@/components/UserParcelsSection";
import DataSourcesPanel from "@/components/admin/DataSourcesPanel";
import ConventionsPanel from "@/components/admin/ConventionsPanel";
import LockRequestsPanel from "@/components/admin/LockRequestsPanel";
import CulturesPanel from "@/components/admin/CulturesPanel";
import AiStatusBanner from "@/components/admin/AiStatusBanner";
import { usePendingLockRequests, PendingBadge } from "@/hooks/usePendingLockRequests";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPER_ADMIN = "bouranepro@gmail.com";

function StatsOverview({ stats }) {
  if (!stats) return null;
  const cards = [
    { label: "Utilisateurs", value: stats.total_users, icon: Users, color: "#2D6A4F" },
    { label: "Abonnés Pro", value: stats.pro_subscriptions, icon: TrendingUp, color: "#2D6A4F" },
    { label: "Acces manuels", value: stats.manual_access_count, icon: Users, color: "#3B82F6" },
    { label: "Parcelles", value: stats.total_parcels, icon: BarChart3, color: "#E07A5F" },
    { label: "Revenu total", value: `${stats.total_revenue}€`, icon: CreditCard, color: "#2D6A4F" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {cards.map((c, i) => (
        <div key={c.label} className="p-3 rounded-xl border border-black/5 bg-white" data-testid={`admin-stat-${i}`}>
          <div className="flex items-center gap-2 mb-1">
            <c.icon className="w-3.5 h-3.5" style={{ color: c.color }} />
            <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: "#7A7A7A" }}>{c.label}</span>
          </div>
          <p className="text-xl font-bold" style={{ fontFamily: "Manrope, sans-serif", color: "#1A1A1A" }}>{c.value}</p>
        </div>
      ))}
    </div>
  );
}

function UsersChart({ chartData }) {
  if (!chartData || chartData.length === 0) return <p className="text-sm text-center py-6" style={{ color: "#7A7A7A" }}>Pas de données</p>;
  const maxVal = Math.max(...chartData.map(d => d.total_users), 1);
  const last14 = chartData.slice(-14);
  return (
    <div className="space-y-2">
      <div className="flex items-end gap-1 h-32">
        {last14.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full rounded-t" style={{
              height: `${Math.max(4, (d.total_users / maxVal) * 100)}%`,
              background: d.new_users > 0 ? "#2D6A4F" : "#E5E5E0",
              minHeight: "4px",
            }} title={`${d.date}: +${d.new_users} (total: ${d.total_users})`} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[9px]" style={{ color: "#7A7A7A" }}>
        <span>{last14[0]?.date?.slice(5)}</span>
        <span>{last14[last14.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
}

// Retour visible sur l'e-mail « accès PRO activé » envoyé à l'utilisateur
const notifyProEmail = (data) => {
  if (data?.email_status === "sent") toast.success("E-mail envoyé", { description: `L'utilisateur a été prévenu par e-mail (${data.email_to}).` });
  else if (data?.email_status === "failed") toast.error("E-mail non envoyé", { description: "L'accès est bien activé, mais l'e-mail n'a pas pu partir. Utilisez « Renvoyer » dans la fiche." });
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const pendingLocks = usePendingLockRequests(true);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stripeConfig, setStripeConfig] = useState({ stripe_secret_key: "", stripe_publishable_key: "", configured: false });
  const [stripeForm, setStripeForm] = useState({ stripe_secret_key: "", stripe_publishable_key: "" });
  const [savingStripe, setSavingStripe] = useState(false);
  const [aiConfig, setAiConfig] = useState({ openai_api_key_masked: "", configured: false });
  const [aiForm, setAiForm] = useState({ openai_api_key: "" });
  const [savingAI, setSavingAI] = useState(false);
  const [testingAI, setTestingAI] = useState(false);
  const [aiCanEdit, setAiCanEdit] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  // --- Onglet Utilisateurs : administration des comptes ---
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [accountBusy, setAccountBusy] = useState(null);
  const [resetLink, setResetLink] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", role: "user", mode: "generate", password: "" });
  const [creating, setCreating] = useState(false);
  const [createdTempPassword, setCreatedTempPassword] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const meRes = await authFetch(`${API_BASE}/auth/me`);
        if (!meRes.ok) { navigate("/", { replace: true }); return; }
        const me = await meRes.json();
        setCurrentUser(me);

        const isAdmin = me.is_admin || me.email === SUPER_ADMIN;
        if (!isAdmin) { navigate("/dashboard", { replace: true }); return; }

        const [statsRes, chartRes, usersRes] = await Promise.all([
          authFetch(`${API_BASE}/admin/stats/overview`).then(r => r.json()),
          authFetch(`${API_BASE}/admin/stats/users-per-day`).then(r => r.json()),
          authFetch(`${API_BASE}/admin/all-users`).then(r => r.json()),
        ]);
        setStats(statsRes);
        setChartData(chartRes);
        setUsers(usersRes);

        if (me.email === SUPER_ADMIN) {
          const configRes = await authFetch(`${API_BASE}/admin/stripe-config`).then(r => r.json()).catch(() => ({}));
          setStripeConfig(configRes);
        }

        // Config IA : accessible à tout super-admin (contrôle côté backend). Si 403, l'onglet reste en lecture.
        try {
          const aiRes = await authFetch(`${API_BASE}/admin/ai-config`);
          if (aiRes.ok) { setAiConfig(await aiRes.json()); setAiCanEdit(true); }
        } catch { /* non super-admin */ }
      } catch { toast.error("Erreur de chargement"); }
      setLoading(false);
    };
    init();
  }, [navigate]);

  const reloadUsers = async () => {
    try {
      const res = await authFetch(`${API_BASE}/admin/all-users`);
      if (res.ok) setUsers(await res.json());
    } catch { /* silencieux : la liste reste sur son dernier état connu */ }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label || "Copié");
    } catch { toast.error("Impossible de copier"); }
  };

  const handleSaveStripe = async () => {
    if (!stripeForm.stripe_secret_key) { toast.error("Clé secrète requise"); return; }
    setSavingStripe(true);
    try {
      const res = await authFetch(`${API_BASE}/admin/stripe-config`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(stripeForm),
      });
      if (!res.ok) throw new Error();
      toast.success("Configuration Stripe sauvegardée");
      setStripeConfig({ ...stripeConfig, configured: true, stripe_publishable_key: stripeForm.stripe_publishable_key });
      setStripeForm({ stripe_secret_key: "", stripe_publishable_key: "" });
    } catch { toast.error("Erreur de sauvegarde"); }
    setSavingStripe(false);
  };

  const handleSaveAI = async () => {
    if (!aiForm.openai_api_key) { toast.error("Clé API requise"); return; }
    setSavingAI(true);
    try {
      const res = await authFetch(`${API_BASE}/admin/ai-config`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(aiForm),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || ""); }
      toast.success("Clé API IA sauvegardée");
      setAiConfig({ ...aiConfig, configured: true, openai_api_key_masked: `${aiForm.openai_api_key.slice(0, 6)}...${aiForm.openai_api_key.slice(-4)}` });
      setAiForm({ openai_api_key: "" });
    } catch (e) { toast.error(e.message || "Erreur de sauvegarde"); }
    setSavingAI(false);
  };

  const handleTestAI = async () => {
    setTestingAI(true);
    try {
      const res = await authFetch(`${API_BASE}/admin/ai-config/test`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) toast.success(data.message || "Clé valide");
      else toast.error(data.message || data.detail || "Clé invalide");
    } catch { toast.error("Erreur lors du test"); }
    setTestingAI(false);
  };

  const handleToggleAdmin = async (userId, currentAdmin) => {
    setTogglingId(userId);
    try {
      const res = await authFetch(`${API_BASE}/admin/toggle-admin`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, is_admin: !currentAdmin }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, is_admin: !currentAdmin } : u));
      toast.success(!currentAdmin ? "Admin activé" : "Admin désactivé");
      await reloadUsers();
    } catch (e) { toast.error(e.message || "Erreur"); }
    setTogglingId(null);
  };

  const handleToggleCoordinator = async (u) => {
    setTogglingId(u.user_id);
    try {
      const res = await authFetch(`${API_BASE}/admin/toggle-coordinator`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: u.user_id, is_coordinator: !u.is_coordinator }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      setUsers(prev => prev.map(x => x.user_id === u.user_id ? { ...x, is_coordinator: !u.is_coordinator } : x));
      toast.success(!u.is_coordinator ? "Rôle coordinateur activé" : "Rôle coordinateur retiré");
      await reloadUsers();
    } catch (e) { toast.error(e.message || "Erreur"); }
    setTogglingId(null);
  };

  const handleGrantPlan = async (userId, planType) => {
    setTogglingId(userId);
    try {
      const res = await authFetch(`${API_BASE}/stripe/admin/grant-plan`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, plan_type: planType }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setUsers(prev => prev.map(u => u.user_id === userId ? {
        ...u,
        manual_access: true,
        subscription_plan: planType,
        subscription_status: "active",
      } : u));
      toast.success(data.message || "Plan attribue");
      notifyProEmail(data);
      await reloadUsers();
    } catch { toast.error("Erreur"); }
    setTogglingId(null);
  };

  const handleResendProEmail = async (userId) => {
    setTogglingId(userId);
    try {
      const res = await authFetch(`${API_BASE}/stripe/admin/resend-pro-email/${userId}`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Envoi impossible");
      notifyProEmail(data);
      await reloadUsers();
    } catch (e) { toast.error("E-mail non envoyé", { description: e.message }); }
    setTogglingId(null);
  };

  const handleRevokeAccess = async (userId) => {
    setTogglingId(userId);
    try {
      const res = await authFetch(`${API_BASE}/stripe/admin/toggle-access`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, manual_access: false }),
      });
      if (!res.ok) throw new Error();
      setUsers(prev => prev.map(u => u.user_id === userId ? {
        ...u,
        manual_access: false,
        subscription_plan: null,
        subscription_status: "inactive",
      } : u));
      toast.success("Acces revoque");
      await reloadUsers();
    } catch { toast.error("Erreur"); }
    setTogglingId(null);
  };

  // --- Actions de compte (super-admin) ---
  const handleResetPassword = async (userId) => {
    setAccountBusy(`reset-${userId}`);
    setResetLink(null);
    try {
      const res = await authFetch(`${API_BASE}/admin/users/${userId}/reset-password`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Erreur");
      setResetLink(data.reset_link || "");
      toast.success("Lien de réinitialisation généré");
    } catch (e) { toast.error(e.message || "Erreur"); }
    setAccountBusy(null);
  };

  const handleSendConvention = async (u) => {
    if (!window.confirm(`Envoyer à ${u.email} l'e-mail « Continuer vers la signature » de la convention ?`)) return;
    setAccountBusy(`conv-${u.user_id}`);
    try {
      const res = await authFetch(`${API_BASE}/admin/users/${u.user_id}/send-convention-email`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Erreur");
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, convention_reminder_sent_at: data.sent_at } : x)));
      toast.success(`E-mail envoyé à ${u.email}`);
    } catch (e) { toast.error(e.message || "Erreur"); }
    setAccountBusy(null);
  };

  const handleSuspend = async (userId, suspend) => {
    setAccountBusy(`suspend-${userId}`);
    try {
      const res = await authFetch(`${API_BASE}/admin/users/${userId}/suspend`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ suspended: suspend }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Erreur");
      toast.success(suspend ? "Compte suspendu" : "Compte réactivé");
      await reloadUsers();
    } catch (e) { toast.error(e.message || "Erreur"); }
    setAccountBusy(null);
  };

  const handleAnonymize = async (userId) => {
    if (!window.confirm("Anonymiser cet utilisateur (RGPD) ? Les données personnelles seront effacées. Action irréversible.")) return;
    setAccountBusy(`anon-${userId}`);
    try {
      const res = await authFetch(`${API_BASE}/admin/users/${userId}/anonymize`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Erreur");
      toast.success("Utilisateur anonymisé");
      setSelectedUserId(null);
      await reloadUsers();
    } catch (e) { toast.error(e.message || "Erreur"); }
    setAccountBusy(null);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Supprimer définitivement cet utilisateur ? Action irréversible.")) return;
    setAccountBusy(`del-${userId}`);
    try {
      const res = await authFetch(`${API_BASE}/admin/users/${userId}`, { method: "DELETE" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Erreur");
      toast.success("Utilisateur supprimé");
      setSelectedUserId(null);
      await reloadUsers();
    } catch (e) { toast.error(e.message || "Erreur"); }
    setAccountBusy(null);
  };

  const resetAddForm = () => {
    setAddForm({ name: "", email: "", role: "user", mode: "generate", password: "" });
    setCreatedTempPassword(null);
  };

  const handleCreateUser = async () => {
    const email = (addForm.email || "").trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("E-mail invalide"); return; }
    if (addForm.mode === "password" && (addForm.password || "").length < 8) { toast.error("Mot de passe : 8 caractères minimum"); return; }
    setCreating(true);
    try {
      const body = { name: addForm.name.trim(), email, role: addForm.role };
      if (addForm.mode === "password" && addForm.password) body.password = addForm.password;
      const res = await authFetch(`${API_BASE}/admin/users`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Erreur");
      await reloadUsers();
      if (data.temp_password) {
        setCreatedTempPassword(data.temp_password);
        toast.success("Compte créé — mot de passe temporaire généré");
      } else {
        toast.success("Compte créé");
        setShowAddModal(false);
        resetAddForm();
      }
    } catch (e) { toast.error(e.message || "Erreur"); }
    setCreating(false);
  };

  const getStatusBadge = (u) => {
    if (u.email === SUPER_ADMIN) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#2D6A4F20", color: "#2D6A4F" }}><Shield className="w-3 h-3" />Super Admin</span>;
    if (u.is_admin) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#8B5CF620", color: "#8B5CF6" }}><ShieldCheck className="w-3 h-3" />Admin</span>;
    if (u.manual_access) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#3B82F620", color: "#3B82F6" }}><ShieldCheck className="w-3 h-3" />Acces manuel</span>;
    if ((u.subscription_plan === "veyer_ciel_sub" || u.subscription_plan === "veyer_ciel_pro") && u.subscription_status === "active") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#2D6A4F20", color: "#2D6A4F" }}><CreditCard className="w-3 h-3" />Pro</span>;
    if (u.subscription_status === "expired") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#EF444420", color: "#EF4444" }}>Expire</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#6B728020", color: "#6B7280" }}>Inactif</span>;
  };

  const isSuper = (u) => u?.email === SUPER_ADMIN;
  const isSuperViewer = currentUser?.email === SUPER_ADMIN;

  const formatDateFR = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  };

  const STATUS_META = {
    active: { label: "Actif", color: "#10B981" },
    suspended: { label: "Suspendu", color: "#EF4444" },
    inactive: { label: "Inactif", color: "#6B7280" },
  };
  const getStatusKey = (u) => {
    if (u.suspended) return "suspended";
    const active = u.is_admin || isSuper(u) || u.manual_access || u.subscription_status === "active";
    return active ? "active" : "inactive";
  };
  const getPlanKey = (u) => (u.subscription_plan === "veyer_ciel_sub" || u.subscription_plan === "veyer_ciel_pro") ? "pro" : u.subscription_plan === "veyer_ciel" ? "basic" : "none";
  const isAdminRole = (u) => u.is_admin || isSuper(u);

  const StatusPill = ({ u }) => {
    const m = STATUS_META[getStatusKey(u)];
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: m.color }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />{m.label}
      </span>
    );
  };

  const filteredUsers = users.filter((u) => {
    const q = userSearch.trim().toLowerCase();
    if (q && !(`${u.name || ""}`.toLowerCase().includes(q) || `${u.email || ""}`.toLowerCase().includes(q))) return false;
    if (roleFilter === "admin" && !isAdminRole(u)) return false;
    if (roleFilter === "user" && isAdminRole(u)) return false;
    if (planFilter !== "all" && getPlanKey(u) !== planFilter) return false;
    if (statusFilter !== "all" && getStatusKey(u) !== statusFilter) return false;
    return true;
  });

  const selectedUser = users.find((u) => u.user_id === selectedUserId) || null;
  const closeDrawer = () => { setSelectedUserId(null); setResetLink(null); };
  const openDrawer = (u) => { setSelectedUserId(u.user_id); setResetLink(null); };

  const selectStyle = { color: "#4A4A4A", borderColor: "#E5E5E0", background: "#fff" };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#FAFAF8" }}><Loader2 className="w-8 h-8 animate-spin" style={{ color: "#2D6A4F" }} /></div>;

  const tabs = [
    { id: "overview", label: "Vue d'ensemble", icon: BarChart3 },
    { id: "users", label: "Utilisateurs", icon: Users },
    { id: "lock-requests", label: "Demandes de verrou", icon: Lock },
    { id: "conventions", label: "Conventions", icon: Mail },
    { id: "data", label: "Données", icon: Database },
    { id: "cultures", label: "Cultures", icon: Leaf },
    { id: "stripe", label: "Stripe", icon: CreditCard },
    { id: "ai", label: "Clé API", icon: Key },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#FAFAF8" }} data-testid="admin-dashboard">
      {/* Header */}
      <header className="p-4 flex items-center gap-3 border-b bg-white" style={{ borderColor: "#E5E5E0" }}>
        <button onClick={() => navigate("/dashboard")} className="p-1.5 rounded-lg hover:bg-black/5" data-testid="admin-back-btn">
          <ArrowLeft className="w-5 h-5" style={{ color: "#1A1A1A" }} />
        </button>
        <img src="/veyer-ciel-logo.png" alt="Véyèr CIEL" className="h-8" />
        <div className="flex-1">
          <h1 className="text-base font-bold" style={{ fontFamily: "Manrope, sans-serif", color: "#1A1A1A" }}>Tableau de bord</h1>
          <p className="text-[11px]" style={{ color: "#7A7A7A" }}>Administration Véyèr CIEL</p>
        </div>
        <Settings className="w-5 h-5" style={{ color: "#7A7A7A" }} />
      </header>

      {/* Tabs */}
      <div className="flex border-b bg-white px-4 gap-1 overflow-x-auto" style={{ borderColor: "#E5E5E0" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap shrink-0 ${tab === t.id ? "border-[#2D6A4F]" : "border-transparent"}`}
            style={{ color: tab === t.id ? "#2D6A4F" : "#7A7A7A" }}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
            {t.id === "lock-requests" && <PendingBadge count={pendingLocks} />}
          </button>
        ))}
      </div>

      <AiStatusBanner canCheck={aiCanEdit} onOpenKeyTab={() => setTab("ai")} />

      {/* Content */}
      <ScrollArea className="flex-1 p-4">
        {tab === "overview" && (
          <div className="max-w-3xl mx-auto space-y-6">
            <StatsOverview stats={stats} />
            <div className="rounded-xl border border-black/5 bg-white p-4 space-y-3">
              <h3 className="text-sm font-bold" style={{ color: "#1A1A1A" }}>Inscriptions (14 derniers jours)</h3>
              <UsersChart chartData={chartData} />
            </div>
          </div>
        )}

        {tab === "users" && (
          <div className="max-w-5xl mx-auto space-y-3">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-[200px] rounded-xl border bg-white px-3 py-2" style={{ borderColor: "#E5E5E0" }}>
                <Search className="w-4 h-4 shrink-0" style={{ color: "#7A7A7A" }} />
                <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Rechercher par nom ou e-mail…"
                  className="w-full bg-transparent outline-none text-sm" style={{ color: "#1A1A1A" }}
                  data-testid="admin-user-search" />
              </div>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
                className="rounded-xl border px-3 py-2 text-xs font-semibold outline-none" style={selectStyle}
                data-testid="admin-filter-role">
                <option value="all">Rôle : tous</option>
                <option value="admin">Admin</option>
                <option value="user">Utilisateur</option>
              </select>
              <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
                className="rounded-xl border px-3 py-2 text-xs font-semibold outline-none" style={selectStyle}
                data-testid="admin-filter-plan">
                <option value="all">Plan : tous</option>
                <option value="pro">Pro</option>
                <option value="none">Sans abonnement</option>
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-xl border px-3 py-2 text-xs font-semibold outline-none" style={selectStyle}
                data-testid="admin-filter-status">
                <option value="all">Statut : tous</option>
                <option value="active">Actif</option>
                <option value="suspended">Suspendu</option>
                <option value="inactive">Inactif</option>
              </select>
              {isSuperViewer && (
                <Button onClick={() => { resetAddForm(); setShowAddModal(true); }}
                  className="h-9 text-xs font-semibold rounded-xl text-white gap-1.5 px-3" style={{ background: "#2D6A4F" }}
                  data-testid="admin-add-user-btn">
                  <Plus className="w-4 h-4" />Ajouter un utilisateur
                </Button>
              )}
            </div>

            <p className="text-xs font-medium" style={{ color: "#7A7A7A" }}>
              {filteredUsers.length} / {users.length} utilisateur{users.length > 1 ? "s" : ""}
            </p>

            <div className="rounded-xl border border-black/5 bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left" style={{ minWidth: "760px", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#FAFAF8" }}>
                      {["Utilisateur", "Rôle", "Abonnement", "Statut", "Inscrit", "Dernière connexion", ""].map((h, ix) => (
                        <th key={ix} className="text-[10px] font-bold uppercase tracking-wider px-3 py-2.5 border-b"
                          style={{ color: "#7A7A7A", borderColor: "#E5E5E0", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 && (
                      <tr><td colSpan={7} className="px-3 py-6 text-center text-sm" style={{ color: "#7A7A7A" }}>
                        Aucun utilisateur ne correspond aux filtres.
                      </td></tr>
                    )}
                    {filteredUsers.map((u, i) => (
                      <tr key={u.user_id || i} onClick={() => openDrawer(u)}
                        className="cursor-pointer hover:bg-black/[0.02] transition-colors border-b"
                        style={{ borderColor: "#F0EEE9" }} data-testid={`admin-user-${i}`}>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2.5">
                            {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-black/5 shrink-0" /> :
                              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: "#2D6A4F15", color: "#2D6A4F" }}>{(u.name || u.email || "?")[0].toUpperCase()}</div>}
                            <div className="min-w-0">
                              <p className="text-sm font-semibold truncate" style={{ color: "#1A1A1A", maxWidth: "190px" }}>{u.name || "Sans nom"}</p>
                              <p className="text-[11px] truncate" style={{ color: "#7A7A7A", maxWidth: "190px" }}>{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          {u.email === SUPER_ADMIN
                            ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap" style={{ background: "#8B5CF620", color: "#8B5CF6" }}><Shield className="w-3 h-3" />Super-admin</span>
                            : isAdminRole(u)
                              ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap" style={{ background: "#8B5CF620", color: "#8B5CF6" }}><ShieldCheck className="w-3 h-3" />Admin</span>
                              : u.is_coordinator
                                ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap" style={{ background: "#0891B218", color: "#0E7490" }}><Mail className="w-3 h-3" />Coordinateur</span>
                                : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#6B728015", color: "#6B7280" }}>Utilisateur</span>}
                        </td>
                        <td className="px-3 py-2.5">
                          {(u.subscription_plan === "veyer_ciel_sub" || u.subscription_plan === "veyer_ciel_pro")
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap" style={{ background: "#2D6A4F20", color: "#2D6A4F" }}>Pro</span>
                            : u.manual_access
                                ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap" style={{ background: "#3B82F620", color: "#3B82F6" }}>Accès manuel</span>
                                : <span className="text-xs" style={{ color: "#B0ABA0" }}>—</span>}
                        </td>
                        <td className="px-3 py-2.5"><StatusPill u={u} /></td>
                        <td className="px-3 py-2.5 text-[11px] tabular-nums" style={{ color: "#7A7A7A", whiteSpace: "nowrap" }}>{formatDateFR(u.created_at) || "—"}</td>
                        <td className="px-3 py-2.5 text-[11px] tabular-nums" style={{ color: "#7A7A7A", whiteSpace: "nowrap" }}>{formatDateFR(u.last_login_at) || "jamais"}</td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap">
                          {u.email !== SUPER_ADMIN && (
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteUser(u.user_id); }}
                              disabled={accountBusy === `del-${u.user_id}`}
                              className="p-1.5 rounded-lg hover:bg-red-50 align-middle" title="Supprimer l'utilisateur"
                              data-testid={`table-del-${i}`}>
                              {accountBusy === `del-${u.user_id}` ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#EF4444" }} /> : <Trash2 className="w-4 h-4" style={{ color: "#EF4444" }} />}
                            </button>
                          )}
                          <span className="text-lg align-middle ml-1" style={{ color: "#C8C3B8" }}>›</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "data" && <DataSourcesPanel />}
        {tab === "cultures" && <CulturesPanel />}

        {tab === "stripe" && (
          <div className="max-w-lg mx-auto space-y-6">
            <div className="rounded-xl border border-black/5 bg-white p-5 space-y-4">
              <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: "#1A1A1A" }}>
                <Key className="w-4 h-4" style={{ color: "#2D6A4F" }} />
                Configuration Stripe
              </h3>
              <p className="text-xs" style={{ color: "#7A7A7A" }}>
                Connectez votre compte Stripe pour recevoir les paiements. Les clés sont chiffrées et stockées en base.
              </p>
              {stripeConfig.configured && (
                <div className="p-3 rounded-lg" style={{ background: "#10B98110" }}>
                  <p className="text-xs font-medium" style={{ color: "#10B981" }}>Stripe connecté</p>
                  <p className="text-[10px] mt-1" style={{ color: "#7A7A7A" }}>Clé: {stripeConfig.stripe_secret_key_masked}</p>
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: "#4A4A4A" }}>Clé secrète Stripe (sk_live_... ou sk_test_...)</label>
                  <PasswordInput value={stripeForm.stripe_secret_key} onChange={e => setStripeForm(p => ({ ...p, stripe_secret_key: e.target.value }))}
                    placeholder="sk_live_..." className="text-sm" data-testid="stripe-secret-input" />
                </div>
                <div>
                  <label className="text-[11px] font-medium block mb-1" style={{ color: "#4A4A4A" }}>Clé publique (pk_live_...) — optionnel</label>
                  <Input value={stripeForm.stripe_publishable_key} onChange={e => setStripeForm(p => ({ ...p, stripe_publishable_key: e.target.value }))}
                    placeholder="pk_live_..." className="text-sm" data-testid="stripe-public-input" />
                </div>
                <Button onClick={handleSaveStripe} disabled={savingStripe || !stripeForm.stripe_secret_key}
                  className="w-full h-10 text-sm font-semibold rounded-xl text-white gap-2" style={{ background: "#2D6A4F" }}
                  data-testid="save-stripe-btn">
                  {savingStripe ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Sauvegarder
                </Button>
              </div>
            </div>
            <div className="rounded-xl border border-black/5 bg-white p-5 space-y-3">
              <h3 className="text-sm font-bold" style={{ color: "#1A1A1A" }}>Abonnements configurés</h3>
              <div className="space-y-2">
                <div className="p-3 rounded-lg border-2" style={{ borderColor: "#2D6A4F" }}>
                  <p className="text-sm font-semibold" style={{ color: "#1A1A1A" }}>Véyèr CIEL Pro</p>
                  <p className="text-xs" style={{ color: "#7A7A7A" }}>Accès complet + verrouillage de 5 parcelles</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "lock-requests" && <LockRequestsPanel />}
        {tab === "conventions" && <ConventionsPanel canDelete={currentUser?.email === SUPER_ADMIN} />}

        {tab === "ai" && (
          <div className="max-w-lg mx-auto space-y-6">
            <div className="rounded-xl border border-black/5 bg-white p-5 space-y-4">
              <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: "#1A1A1A" }}>
                <Key className="w-4 h-4" style={{ color: "#2D6A4F" }} />
                Clé API — Fonctionnalités IA
              </h3>
              <p className="text-xs" style={{ color: "#7A7A7A" }}>
                Clé OpenAI utilisée par l'analyse agronomique, la détection satellite, le carnet et l'import du registre. Stockée en base, jamais réaffichée en clair.
              </p>
              {!aiCanEdit ? (
                <div className="p-3 rounded-lg" style={{ background: "#EF444410" }}>
                  <p className="text-xs font-medium" style={{ color: "#EF4444" }}>Réservé aux super-administrateurs</p>
                </div>
              ) : (
                <>
                  {aiConfig.configured && (
                    <div className="p-3 rounded-lg" style={{ background: "#10B98110" }}>
                      <p className="text-xs font-medium" style={{ color: "#10B981" }}>Clé configurée</p>
                      <p className="text-[10px] mt-1" style={{ color: "#7A7A7A" }}>Clé: {aiConfig.openai_api_key_masked}</p>
                    </div>
                  )}
                  <div className="space-y-3">
                    <div>
                      <label className="text-[11px] font-medium block mb-1" style={{ color: "#4A4A4A" }}>Clé API OpenAI (sk-...)</label>
                      <PasswordInput value={aiForm.openai_api_key} onChange={e => setAiForm({ openai_api_key: e.target.value })}
                        placeholder="sk-..." className="text-sm" data-testid="ai-key-input" />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleSaveAI} disabled={savingAI || !aiForm.openai_api_key}
                        className="flex-1 h-10 text-sm font-semibold rounded-xl text-white gap-2" style={{ background: "#2D6A4F" }}
                        data-testid="save-ai-btn">
                        {savingAI ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Sauvegarder
                      </Button>
                      <Button onClick={handleTestAI} disabled={testingAI || !aiConfig.configured}
                        variant="outline" className="h-10 text-sm font-semibold rounded-xl gap-2"
                        data-testid="test-ai-btn">
                        {testingAI ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tester"}
                      </Button>
                    </div>
                    <p className="text-[10px]" style={{ color: "#7A7A7A" }}>
                      Clé disponible sur platform.openai.com. Modèles : gpt-4o (vision) et gpt-4o-mini (texte).
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </ScrollArea>

      {/* ============ DRAWER DÉTAIL UTILISATEUR ============ */}
      {selectedUser && (
        <>
          <div className="fixed inset-0 z-40" style={{ background: "rgba(26,26,23,0.28)" }} onClick={closeDrawer} data-testid="admin-drawer-scrim" />
          <aside className="fixed top-0 right-0 h-full z-50 bg-white flex flex-col border-l"
            style={{ width: "min(430px,94vw)", borderColor: "#E5E5E0", boxShadow: "-24px 0 48px -24px rgba(26,26,23,0.3)" }}
            data-testid="admin-user-drawer">
            {/* header */}
            <div className="p-4 border-b flex items-start gap-3" style={{ borderColor: "#E5E5E0" }}>
              {selectedUser.picture ? <img src={selectedUser.picture} alt="" className="w-11 h-11 rounded-full object-cover border border-black/5" /> :
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-base font-bold" style={{ background: "#2D6A4F15", color: "#2D6A4F" }}>{(selectedUser.name || selectedUser.email || "?")[0].toUpperCase()}</div>}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold truncate" style={{ fontFamily: "Manrope, sans-serif", color: "#1A1A1A" }}>{selectedUser.name || "Sans nom"}</h3>
                <p className="text-[11px] truncate" style={{ color: "#7A7A7A" }}>{selectedUser.email}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">{getStatusBadge(selectedUser)}<StatusPill u={selectedUser} /></div>
              </div>
              <button onClick={closeDrawer} className="p-1.5 rounded-lg hover:bg-black/5 shrink-0" data-testid="admin-drawer-close">
                <X className="w-4 h-4" style={{ color: "#7A7A7A" }} />
              </button>
            </div>

            {/* body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Identité */}
              <div className="rounded-xl border border-black/5">
                <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider border-b" style={{ color: "#7A7A7A", borderColor: "#E5E5E0", background: "#FAFAF8" }}>Identité</div>
                <div className="p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span style={{ color: "#7A7A7A" }}>Inscrit le</span>
                    <span className="font-semibold" style={{ color: "#1A1A1A" }}>{formatDateFR(selectedUser.created_at) || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span style={{ color: "#7A7A7A" }}>Dernière connexion</span>
                    <span className="font-semibold" style={{ color: "#1A1A1A" }}>{formatDateFR(selectedUser.last_login_at) || "Jamais"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span style={{ color: "#7A7A7A" }}>Statut</span>
                    <StatusPill u={selectedUser} />
                  </div>
                </div>
              </div>

              {!isSuper(selectedUser) && (
                <UserParcelsSection userId={selectedUser.user_id} onOpenMap={() => navigate(`/map?attach_for=${selectedUser.user_id}`)} />
              )}

              {isSuper(selectedUser) ? (
                <div className="p-3 rounded-xl flex items-start gap-2" style={{ background: "#2D6A4F10" }}>
                  <Shield className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#2D6A4F" }} />
                  <p className="text-[11px]" style={{ color: "#2D6A4F" }}>Compte super-administrateur protégé : suspension, anonymisation et suppression sont désactivées.</p>
                </div>
              ) : (
                <>
                  {/* Rôle & accès */}
                  <div className="rounded-xl border border-black/5">
                    <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider border-b" style={{ color: "#7A7A7A", borderColor: "#E5E5E0", background: "#FAFAF8" }}>Rôle &amp; accès</div>
                    <div className="p-3 space-y-3">
                      {isSuperViewer && (
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-xs font-semibold" style={{ color: "#1A1A1A" }}>Rôle administrateur</p>
                            <p className="text-[11px]" style={{ color: "#7A7A7A" }}>Accès au tableau de bord admin</p>
                          </div>
                          <button onClick={() => handleToggleAdmin(selectedUser.user_id, selectedUser.is_admin)} disabled={togglingId === selectedUser.user_id}
                            className="shrink-0" style={{ color: selectedUser.is_admin ? "#8B5CF6" : "#9A948A" }} data-testid={`toggle-admin-${selectedUser.user_id}`}>
                            {togglingId === selectedUser.user_id ? <Loader2 className="w-6 h-6 animate-spin" /> : selectedUser.is_admin ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
                          </button>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-xs font-semibold" style={{ color: "#1A1A1A" }}>Coordinateur</p>
                          <p className="text-[11px]" style={{ color: "#7A7A7A" }}>Fait signer les conventions aux producteurs et consulte le registre des conventions</p>
                        </div>
                        <button onClick={() => handleToggleCoordinator(selectedUser)} disabled={togglingId === selectedUser.user_id}
                          className="shrink-0" style={{ color: selectedUser.is_coordinator ? "#0E7490" : "#9A948A" }} data-testid={`toggle-coordinator-${selectedUser.user_id}`}>
                          {togglingId === selectedUser.user_id ? <Loader2 className="w-6 h-6 animate-spin" /> : selectedUser.is_coordinator ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
                        </button>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-xs font-semibold" style={{ color: "#1A1A1A" }}>Convention</p>
                          <p className="text-[11px]" style={{ color: selectedUser.convention_signed_at ? "#10B981" : "#7A7A7A" }}>
                            {selectedUser.convention_signed_at
                              ? `Signée le ${formatDateFR(selectedUser.convention_signed_at)}`
                              : `Non signée${selectedUser.convention_reminder_sent_at ? ` · e-mail envoyé le ${formatDateFR(selectedUser.convention_reminder_sent_at)}` : ""}`}
                          </p>
                        </div>
                        {!selectedUser.convention_signed_at && (
                          <button onClick={() => handleSendConvention(selectedUser)} disabled={accountBusy === `conv-${selectedUser.user_id}`}
                            title="E-mail « Continuer vers la signature » pour obtenir les accès Pro"
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-black/10 hover:bg-black/5 shrink-0"
                            style={{ color: "#2D6A4F" }} data-testid={`send-convention-${selectedUser.user_id}`}>
                            {accountBusy === `conv-${selectedUser.user_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                            {selectedUser.convention_reminder_sent_at ? "Renvoyer l'e-mail" : "Envoyer l'e-mail"}
                          </button>
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-semibold" style={{ color: "#1A1A1A" }}>Abonnement</p>
                        <p className="text-[11px]" style={{ color: "#7A7A7A" }}>
                          {getPlanKey(selectedUser) === "pro" ? "Véyèr CIEL Pro" : getPlanKey(selectedUser) === "basic" ? "Véyèr CIEL (ancien plan)" : "Aucun abonnement"}
                          {selectedUser.manual_access && " · accès manuel"}
                        </p>
                        {selectedUser.manual_access && (
                          <p className="text-[11px] mt-0.5 flex items-center gap-1.5 flex-wrap" style={{ color: selectedUser.pro_access_email_sent_at ? "#2D6A4F" : "#B4532F" }} data-testid="pro-email-status">
                            <Mail className="w-3 h-3" />
                            {selectedUser.pro_access_email_sent_at
                              ? `E-mail « accès PRO » envoyé le ${new Date(selectedUser.pro_access_email_sent_at).toLocaleDateString("fr-FR")}`
                              : "E-mail « accès PRO » pas encore envoyé"}
                            <button onClick={() => handleResendProEmail(selectedUser.user_id)} disabled={togglingId === selectedUser.user_id}
                              className="underline font-medium" data-testid={`resend-pro-email-${selectedUser.user_id}`}>
                              {selectedUser.pro_access_email_sent_at ? "Renvoyer" : "Envoyer"}
                            </button>
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => handleGrantPlan(selectedUser.user_id, "veyer_ciel_sub")} disabled={togglingId === selectedUser.user_id}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-black/10 hover:bg-black/5"
                          style={{ color: "#2D6A4F" }} data-testid={`grant-sub-${selectedUser.user_id}`}>
                          {togglingId === selectedUser.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Leaf className="w-3.5 h-3.5" />}Attribuer Pro
                        </button>
                        <button onClick={() => handleRevokeAccess(selectedUser.user_id)} disabled={togglingId === selectedUser.user_id}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-black/10 hover:bg-black/5"
                          style={{ color: "#EF4444" }} data-testid={`revoke-access-${selectedUser.user_id}`}>
                          {togglingId === selectedUser.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldX className="w-3.5 h-3.5" />}Révoquer
                        </button>
                      </div>
                    </div>
                  </div>

                  {isSuperViewer && (
                    <>
                      {/* Actions de compte */}
                      <div className="rounded-xl border border-black/5">
                        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider border-b" style={{ color: "#7A7A7A", borderColor: "#E5E5E0", background: "#FAFAF8" }}>Actions de compte</div>
                        <div className="p-3 space-y-3">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="text-xs font-semibold" style={{ color: "#1A1A1A" }}>Réinitialiser le mot de passe</p>
                              <p className="text-[11px]" style={{ color: "#7A7A7A" }}>Génère un lien à transmettre (valide 1h)</p>
                            </div>
                            <button onClick={() => handleResetPassword(selectedUser.user_id)} disabled={accountBusy === `reset-${selectedUser.user_id}`}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-black/10 hover:bg-black/5 shrink-0"
                              style={{ color: "#2D6A4F" }} data-testid={`reset-password-${selectedUser.user_id}`}>
                              {accountBusy === `reset-${selectedUser.user_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-3.5 h-3.5" />}Générer
                            </button>
                          </div>
                          {resetLink !== null && (
                            <div className="p-2 rounded-lg space-y-1.5" style={{ background: "#FAFAF8" }}>
                              <p className="text-[10px] font-medium" style={{ color: "#7A7A7A" }}>Lien de réinitialisation</p>
                              <div className="flex items-center gap-1.5">
                                <input readOnly value={resetLink} className="flex-1 min-w-0 bg-white border rounded-md px-2 py-1 text-[11px]" style={{ borderColor: "#E5E5E0", color: "#4A4A4A" }} data-testid="reset-link-input" />
                                <button onClick={() => copyToClipboard(resetLink, "Lien copié")}
                                  className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-white shrink-0" style={{ background: "#2D6A4F" }} data-testid="reset-link-copy">
                                  <Copy className="w-3.5 h-3.5" />Copier
                                </button>
                              </div>
                            </div>
                          )}
                          <div className="flex items-center justify-between gap-2 pt-1 border-t" style={{ borderColor: "#E5E5E0" }}>
                            <div>
                              <p className="text-xs font-semibold" style={{ color: "#1A1A1A" }}>{selectedUser.suspended ? "Réactiver le compte" : "Suspendre le compte"}</p>
                              <p className="text-[11px]" style={{ color: "#7A7A7A" }}>{selectedUser.suspended ? "Rétablit la connexion" : "Bloque la connexion sans supprimer"}</p>
                            </div>
                            <button onClick={() => handleSuspend(selectedUser.user_id, !selectedUser.suspended)} disabled={accountBusy === `suspend-${selectedUser.user_id}`}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-black/10 hover:bg-black/5 shrink-0"
                              style={{ color: selectedUser.suspended ? "#10B981" : "#8A5A12" }} data-testid={`suspend-${selectedUser.user_id}`}>
                              {accountBusy === `suspend-${selectedUser.user_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedUser.suspended ? <RotateCcw className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                              {selectedUser.suspended ? "Réactiver" : "Suspendre"}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Zone dangereuse */}
                      <div className="rounded-xl border" style={{ borderColor: "#EF444433" }}>
                        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider border-b flex items-center gap-1.5" style={{ color: "#EF4444", borderColor: "#EF444433", background: "#EF444410" }}>
                          <AlertTriangle className="w-3.5 h-3.5" />Zone dangereuse
                        </div>
                        <div className="p-3 space-y-3">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="text-xs font-semibold" style={{ color: "#1A1A1A" }}>Anonymiser (RGPD)</p>
                              <p className="text-[11px]" style={{ color: "#7A7A7A" }}>Efface les données perso, garde le registre légal</p>
                            </div>
                            <button onClick={() => handleAnonymize(selectedUser.user_id)} disabled={accountBusy === `anon-${selectedUser.user_id}`}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border hover:bg-red-50 shrink-0"
                              style={{ color: "#EF4444", borderColor: "#EF444455" }} data-testid={`anonymize-${selectedUser.user_id}`}>
                              {accountBusy === `anon-${selectedUser.user_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserX className="w-3.5 h-3.5" />}Anonymiser
                            </button>
                          </div>
                          <div className="flex items-center justify-between gap-2 pt-1 border-t" style={{ borderColor: "#EF444422" }}>
                            <div>
                              <p className="text-xs font-semibold" style={{ color: "#EF4444" }}>Supprimer l'utilisateur</p>
                              <p className="text-[11px]" style={{ color: "#7A7A7A" }}>Suppression définitive du compte</p>
                            </div>
                            <button onClick={() => handleDeleteUser(selectedUser.user_id)} disabled={accountBusy === `del-${selectedUser.user_id}`}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-white shrink-0"
                              style={{ background: "#EF4444" }} data-testid={`delete-${selectedUser.user_id}`}>
                              {accountBusy === `del-${selectedUser.user_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}Supprimer
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </aside>
        </>
      )}

      {/* ============ MODALE AJOUT UTILISATEUR ============ */}
      {showAddModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-5" style={{ background: "rgba(26,26,23,0.34)" }}
          onClick={(e) => { if (e.target === e.currentTarget && !creating) { setShowAddModal(false); resetAddForm(); } }}>
          <div className="bg-white rounded-2xl w-full max-w-md border" style={{ borderColor: "#E5E5E0", boxShadow: "0 8px 24px -12px rgba(26,26,23,0.3)" }} data-testid="admin-add-user-modal">
            <div className="p-4 border-b flex items-center gap-2" style={{ borderColor: "#E5E5E0" }}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "#2D6A4F15", color: "#2D6A4F" }}><Plus className="w-4 h-4" /></div>
              <h3 className="text-sm font-bold flex-1" style={{ fontFamily: "Manrope, sans-serif", color: "#1A1A1A" }}>Ajouter un utilisateur</h3>
              <button onClick={() => { setShowAddModal(false); resetAddForm(); }} className="p-1.5 rounded-lg hover:bg-black/5" data-testid="admin-add-close">
                <X className="w-4 h-4" style={{ color: "#7A7A7A" }} />
              </button>
            </div>

            {createdTempPassword ? (
              <div className="p-5 space-y-4">
                <div className="p-3 rounded-lg" style={{ background: "#10B98110" }}>
                  <p className="text-xs font-semibold" style={{ color: "#10B981" }}>Compte créé avec succès</p>
                  <p className="text-[11px] mt-1" style={{ color: "#7A7A7A" }}>Un mot de passe temporaire a été généré. Transmettez-le à l'utilisateur — il ne sera plus affiché.</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-medium block" style={{ color: "#4A4A4A" }}>Mot de passe temporaire</label>
                  <div className="flex items-center gap-1.5">
                    <input readOnly value={createdTempPassword} className="flex-1 min-w-0 bg-white border rounded-md px-2 py-1.5 text-sm font-mono" style={{ borderColor: "#E5E5E0", color: "#1A1A1A" }} data-testid="temp-password-input" />
                    <button onClick={() => copyToClipboard(createdTempPassword, "Mot de passe copié")}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-white shrink-0" style={{ background: "#2D6A4F" }} data-testid="temp-password-copy">
                      <Copy className="w-3.5 h-3.5" />Copier
                    </button>
                  </div>
                </div>
                <Button onClick={() => { setShowAddModal(false); resetAddForm(); }}
                  className="w-full h-10 text-sm font-semibold rounded-xl text-white" style={{ background: "#2D6A4F" }} data-testid="admin-add-done">
                  Terminé
                </Button>
              </div>
            ) : (
              <>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="text-[11px] font-medium block mb-1" style={{ color: "#4A4A4A" }}>Nom</label>
                    <Input value={addForm.name} onChange={(e) => setAddForm((p) => ({ ...p, name: e.target.value }))} placeholder="Nom complet" className="text-sm" data-testid="add-name-input" />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium block mb-1" style={{ color: "#4A4A4A" }}>Adresse e-mail</label>
                    <Input value={addForm.email} onChange={(e) => setAddForm((p) => ({ ...p, email: e.target.value }))} placeholder="nom@exemple.re" className="text-sm" type="email" data-testid="add-email-input" />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium block mb-1" style={{ color: "#4A4A4A" }}>Rôle</label>
                    <select value={addForm.role} onChange={(e) => setAddForm((p) => ({ ...p, role: e.target.value }))}
                      className="w-full rounded-lg border px-3 py-2 text-sm outline-none" style={selectStyle} data-testid="add-role-select">
                      <option value="user">Utilisateur</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium block mb-1" style={{ color: "#4A4A4A" }}>Mot de passe</label>
                    <div className="flex rounded-lg border p-0.5 gap-0.5 mb-2" style={{ borderColor: "#E5E5E0", background: "#FAFAF8" }}>
                      <button type="button" onClick={() => setAddForm((p) => ({ ...p, mode: "generate" }))}
                        className="flex-1 text-[11px] font-semibold py-1.5 rounded-md transition-colors"
                        style={{ background: addForm.mode === "generate" ? "#fff" : "transparent", color: addForm.mode === "generate" ? "#1A1A1A" : "#7A7A7A", boxShadow: addForm.mode === "generate" ? "0 1px 2px rgba(0,0,0,0.06)" : "none" }}
                        data-testid="add-mode-generate">Générer un mot de passe</button>
                      <button type="button" onClick={() => setAddForm((p) => ({ ...p, mode: "password" }))}
                        className="flex-1 text-[11px] font-semibold py-1.5 rounded-md transition-colors"
                        style={{ background: addForm.mode === "password" ? "#fff" : "transparent", color: addForm.mode === "password" ? "#1A1A1A" : "#7A7A7A", boxShadow: addForm.mode === "password" ? "0 1px 2px rgba(0,0,0,0.06)" : "none" }}
                        data-testid="add-mode-password">Définir un mot de passe</button>
                    </div>
                    {addForm.mode === "password" ? (
                      <PasswordInput value={addForm.password} onChange={(e) => setAddForm((p) => ({ ...p, password: e.target.value }))} placeholder="8 caractères minimum" className="text-sm" data-testid="add-password-input" />
                    ) : (
                      <p className="text-[10px]" style={{ color: "#7A7A7A" }}>Un mot de passe temporaire sera généré et affiché après la création.</p>
                    )}
                  </div>
                </div>
                <div className="p-4 border-t flex gap-2 justify-end" style={{ borderColor: "#E5E5E0" }}>
                  <Button onClick={() => { setShowAddModal(false); resetAddForm(); }} variant="outline" className="h-10 text-sm font-semibold rounded-xl" data-testid="admin-add-cancel">Annuler</Button>
                  <Button onClick={handleCreateUser} disabled={creating}
                    className="h-10 text-sm font-semibold rounded-xl text-white gap-2" style={{ background: "#2D6A4F" }} data-testid="admin-create-user-submit">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Créer le compte
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
